// frontend/src/components/DateInputBR.js
//
// Campo de data no formato brasileiro dd/mm/aaaa (ETAPA 6d). O campo nativo (type="date") mostra o formato da língua do NAVEGADOR
// (mm/dd/yyyy num Chrome em inglês) e a página não consegue mudar isso. Aqui a pessoa digita com máscara (ou cola, ou escolhe no
// calendário do botão ao lado) e a tela continua trabalhando com a data em AAAA-MM-DD.
//
// Contrato com a tela: o valor devolvido em onChange é
//   - "AAAA-MM-DD" quando o texto é uma data completa e existente;
//   - "" quando o campo está vazio;
//   - o texto digitado ("07/1", "31/02/2026") quando está incompleto ou não existe. A tela DEVE recusar esse valor (não é uma data):
//     um campo opcional nunca pode virar "sem data" só porque a pessoa digitou a data pela metade.

import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import './DateInputBR.css';

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

/** "AAAA-MM-DD" → "dd/mm/aaaa"; qualquer outra coisa → "". */
export const isoToBr = (iso) => (ISO_RE.test(String(iso == null ? '' : iso)) ? String(iso).split('-').reverse().join('/') : '');

/** "dd/mm/aaaa" completo e existente → "AAAA-MM-DD"; senão null. */
export function brToIso(text) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(text == null ? '' : text));
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Texto digitado ou colado → "dd/mm/aaaa" com as barras no lugar (aceita também 2026-11-07 e 7/11/2026 colados). */
export function maskBr(raw) {
  const text = String(raw == null ? '' : raw).trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (iso) return `${iso[3].padStart(2, '0')}/${iso[2].padStart(2, '0')}/${iso[1]}`;
  const loose = /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{4})$/.exec(text);
  if (loose) return `${loose[1].padStart(2, '0')}/${loose[2].padStart(2, '0')}/${loose[3]}`;
  const digits = text.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * @param {Object} props
 * @param {string} [props.id] id do campo de texto (o rótulo da tela aponta para ele).
 * @param {string} [props.value] "AAAA-MM-DD" (ou o texto incompleto devolvido antes por onChange).
 * @param {Function} props.onChange recebe o valor descrito no topo do arquivo.
 * @param {string} [props.min] menor data do calendário (AAAA-MM-DD). A validação do que foi digitado é da tela.
 * @param {string} [props.max] maior data do calendário (AAAA-MM-DD).
 * @param {string} [props.className] classes do campo de texto (a tela passa as dela, para ficar igual aos outros campos).
 * @param {boolean} [props.disabled]
 */
export default function DateInputBR({ id, value = '', onChange, min, max, className = '', disabled = false }) {
  const initial = ISO_RE.test(value) ? isoToBr(value) : String(value || '');
  const [text, setText] = useState(initial);
  const lastEmitted = useRef(value);
  const nativeRef = useRef(null);

  // O valor mudou POR FORA (a tela limpou, restaurou o padrão...): o texto acompanha. O que o próprio campo emitiu não conta.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setText(ISO_RE.test(value) ? isoToBr(value) : String(value || ''));
  }, [value]);

  const emit = (next) => { lastEmitted.current = next; if (onChange) onChange(next); };

  const handleType = (event) => {
    const masked = maskBr(event.target.value);
    setText(masked);
    emit(masked === '' ? '' : (brToIso(masked) || masked));
  };

  const handleNative = (event) => {
    const iso = event.target.value;
    setText(isoToBr(iso));
    emit(iso);
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el || disabled) return;
    try {
      if (typeof el.showPicker === 'function') el.showPicker();
      else { el.focus(); el.click(); }
    } catch (error) {
      el.focus();
      el.click();
    }
  };

  const incomplete = text !== '' && brToIso(text) === null;
  return (
    <div className="datebr">
      <input id={id} className={className} type="text" inputMode="numeric" autoComplete="off" placeholder="dd/mm/aaaa" maxLength={10}
        value={text} disabled={disabled} aria-invalid={incomplete && text.length === 10 ? 'true' : undefined} onChange={handleType} />
      <button type="button" className="datebr-btn" onClick={openPicker} disabled={disabled} aria-label="Abrir calendário" title="Abrir calendário"><Calendar size={16} /></button>
      <input ref={nativeRef} type="date" className="datebr-nativo" tabIndex={-1} aria-hidden="true" value={ISO_RE.test(value) ? value : ''} min={min} max={max} disabled={disabled} onChange={handleNative} />
    </div>
  );
}
